// Secret console — business logic, framework-agnostic, zero React imports.
// See docs/ux/secret-ia.md for the page map this backs.

import type {
  SecretEngine, SecretEngineType, SecretEngineStatus,
  Certificate, CertificateStatus, CertificateType,
  Lease, LeaseStatus,
  EncryptionKey, EncryptionKeyStatus,
} from '@core/types/secret';
import type { Duration } from '@core/types/common';

// ============================================
// Engine type / status display
// ============================================

export const ENGINE_TYPE_LABEL_KEY: Record<string, string> = {
  SECRET_ENGINE_TYPE_UNSPECIFIED: 'secret.engineType.unspecified',
  SECRET_ENGINE_TYPE_KV_V1: 'secret.engineType.kvV1',
  SECRET_ENGINE_TYPE_KV_V2: 'secret.engineType.kvV2',
  SECRET_ENGINE_TYPE_TRANSIT: 'secret.engineType.transit',
  SECRET_ENGINE_TYPE_PKI: 'secret.engineType.pki',
  SECRET_ENGINE_TYPE_DATABASE: 'secret.engineType.database',
  SECRET_ENGINE_TYPE_SSH: 'secret.engineType.ssh',
  SECRET_ENGINE_TYPE_TOTP: 'secret.engineType.totp',
  SECRET_ENGINE_TYPE_AWS: 'secret.engineType.aws',
  SECRET_ENGINE_TYPE_GCP: 'secret.engineType.gcp',
};

export function engineTypeLabelKey(type?: SecretEngineType): string {
  return ENGINE_TYPE_LABEL_KEY[type ?? ''] ?? 'secret.engineType.unspecified';
}

/** Engine types this console has a dedicated workspace for. Others (SSH, TOTP, AWS, GCP) can
 * still be mounted/tuned/unmounted, but have no type-specific detail surface yet. */
export function isSupportedEngineWorkspace(type?: SecretEngineType): type is 'SECRET_ENGINE_TYPE_KV_V1' | 'SECRET_ENGINE_TYPE_KV_V2' | 'SECRET_ENGINE_TYPE_TRANSIT' | 'SECRET_ENGINE_TYPE_PKI' | 'SECRET_ENGINE_TYPE_DATABASE' {
  return type === 'SECRET_ENGINE_TYPE_KV_V1' || type === 'SECRET_ENGINE_TYPE_KV_V2'
    || type === 'SECRET_ENGINE_TYPE_TRANSIT' || type === 'SECRET_ENGINE_TYPE_PKI'
    || type === 'SECRET_ENGINE_TYPE_DATABASE';
}

export function isKvEngine(type?: SecretEngineType): boolean {
  return type === 'SECRET_ENGINE_TYPE_KV_V1' || type === 'SECRET_ENGINE_TYPE_KV_V2';
}

export const ENGINE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'secondary' | 'default'> = {
  SECRET_ENGINE_STATUS_UNSPECIFIED: 'default',
  SECRET_ENGINE_STATUS_ACTIVE: 'success',
  SECRET_ENGINE_STATUS_DISABLED: 'warning',
  SECRET_ENGINE_STATUS_DESTROYING: 'error',
  SECRET_ENGINE_STATUS_DESTROYED: 'secondary',
};

export function engineStatusVariant(status?: SecretEngineStatus): 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  return ENGINE_STATUS_VARIANT[status ?? ''] ?? 'default';
}

export function engineStatusLabelKey(status?: SecretEngineStatus): string {
  switch (status) {
    case 'SECRET_ENGINE_STATUS_ACTIVE': return 'secret.engineStatus.active';
    case 'SECRET_ENGINE_STATUS_DISABLED': return 'secret.engineStatus.disabled';
    case 'SECRET_ENGINE_STATUS_DESTROYING': return 'secret.engineStatus.destroying';
    case 'SECRET_ENGINE_STATUS_DESTROYED': return 'secret.engineStatus.destroyed';
    default: return 'secret.engineStatus.unknown';
  }
}

// ============================================
// Certificate status
// ============================================

export const CERTIFICATE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'secondary' | 'default'> = {
  CERTIFICATE_STATUS_UNSPECIFIED: 'default',
  CERTIFICATE_STATUS_ACTIVE: 'success',
  CERTIFICATE_STATUS_EXPIRED: 'secondary',
  CERTIFICATE_STATUS_REVOKED: 'error',
  CERTIFICATE_STATUS_PENDING: 'warning',
};

export function certificateStatusVariant(status?: CertificateStatus): 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  return CERTIFICATE_STATUS_VARIANT[status ?? ''] ?? 'default';
}

export function certificateStatusLabelKey(status?: CertificateStatus): string {
  switch (status) {
    case 'CERTIFICATE_STATUS_ACTIVE': return 'secret.certStatus.active';
    case 'CERTIFICATE_STATUS_EXPIRED': return 'secret.certStatus.expired';
    case 'CERTIFICATE_STATUS_REVOKED': return 'secret.certStatus.revoked';
    case 'CERTIFICATE_STATUS_PENDING': return 'secret.certStatus.pending';
    default: return 'secret.certStatus.unknown';
  }
}

export function certificateTypeLabelKey(type?: CertificateType): string {
  switch (type) {
    case 'CERTIFICATE_TYPE_ROOT_CA': return 'secret.certType.rootCa';
    case 'CERTIFICATE_TYPE_INTERMEDIATE_CA': return 'secret.certType.intermediateCa';
    case 'CERTIFICATE_TYPE_LEAF': return 'secret.certType.leaf';
    default: return 'secret.certType.unknown';
  }
}

export function canRevokeCertificate(cert: Certificate): boolean {
  return cert.status === 'CERTIFICATE_STATUS_ACTIVE';
}

// ============================================
// Lease status
// ============================================

export const LEASE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'secondary' | 'default'> = {
  LEASE_STATUS_UNSPECIFIED: 'default',
  LEASE_STATUS_ACTIVE: 'success',
  LEASE_STATUS_EXPIRED: 'secondary',
  LEASE_STATUS_REVOKED: 'error',
};

export function leaseStatusVariant(status?: LeaseStatus): 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  return LEASE_STATUS_VARIANT[status ?? ''] ?? 'default';
}

export function leaseStatusLabelKey(status?: LeaseStatus): string {
  switch (status) {
    case 'LEASE_STATUS_ACTIVE': return 'secret.leaseStatus.active';
    case 'LEASE_STATUS_EXPIRED': return 'secret.leaseStatus.expired';
    case 'LEASE_STATUS_REVOKED': return 'secret.leaseStatus.revoked';
    default: return 'secret.leaseStatus.unknown';
  }
}

export function canRenewLease(lease: Lease): boolean {
  return lease.status === 'LEASE_STATUS_ACTIVE' && lease.renewable;
}

export function canRevokeLease(lease: Lease): boolean {
  return lease.status === 'LEASE_STATUS_ACTIVE';
}

// ============================================
// Encryption key status
// ============================================

export function encryptionKeyStatusVariant(status?: EncryptionKeyStatus): 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  switch (status) {
    case 'ENCRYPTION_KEY_STATUS_ACTIVE': return 'success';
    case 'ENCRYPTION_KEY_STATUS_DISABLED': return 'warning';
    case 'ENCRYPTION_KEY_STATUS_ARCHIVED': return 'secondary';
    case 'ENCRYPTION_KEY_STATUS_DESTROYED': return 'error';
    default: return 'default';
  }
}

export function canDeleteEncryptionKey(key: EncryptionKey): boolean {
  return key.deletion_allowed && key.status !== 'ENCRYPTION_KEY_STATUS_DESTROYED';
}

// ============================================
// Formatters
// ============================================

export function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

export function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

/** Formats a duration given in seconds ("3665" -> "1h 1m 5s"). */
export function formatTtlSeconds(seconds?: number): string {
  if (seconds == null || seconds <= 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (!d && !h && s) parts.push(`${s}s`);
  return parts.length > 0 ? parts.join(' ') : '0s';
}

/** Parses a google.protobuf.Duration string ("3600s") into seconds. */
export function durationToSeconds(d?: Duration): number | undefined {
  if (!d) return undefined;
  const m = /^(-?\d+(?:\.\d+)?)s$/.exec(d);
  return m ? Math.round(parseFloat(m[1])) : undefined;
}

export function secondsToDuration(seconds?: number): Duration | undefined {
  if (seconds == null || isNaN(seconds)) return undefined;
  return `${seconds}s`;
}

/** Strips a proto enum's SCREAMING_SNAKE_CASE prefix and lowercases the rest, for building i18n
 * key suffixes from enum values, e.g. "ENCRYPTION_KEY_TYPE_AES256_GCM96" + "ENCRYPTION_KEY_TYPE_"
 * -> "aes256_gcm96". */
export function enumSuffix(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

export function engineDisplayName(engine?: SecretEngine): string {
  if (!engine) return '';
  return engine.name || engine.slug || engine.mount_path;
}

// ============================================
// Path tree (KV browser)
// ============================================

/** Groups flat KV secret paths into a tree by "/" for the browser sidebar. */
export interface PathTreeNode {
  name: string;
  fullPath: string;
  isLeaf: boolean;
  children: PathTreeNode[];
}

export function buildPathTree(paths: string[]): PathTreeNode[] {
  const root: PathTreeNode[] = [];
  for (const path of paths) {
    const segments = path.split('/').filter(Boolean);
    let level = root;
    let acc = '';
    segments.forEach((segment, idx) => {
      acc = acc ? `${acc}/${segment}` : segment;
      const isLeaf = idx === segments.length - 1;
      let node = level.find((n) => n.name === segment);
      if (!node) {
        node = { name: segment, fullPath: acc, isLeaf: false, children: [] };
        level.push(node);
      }
      if (isLeaf) node.isLeaf = true;
      level = node.children;
    });
  }
  return root;
}

// ============================================
// Form validation helpers
// ============================================

export function parseCsvList(value: string): string[] {
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

export function validateMountForm(slug: string, mountPath: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!slug.trim()) errors.slug = 'secret.validation.slugRequired';
  else if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) errors.slug = 'secret.validation.slugFormat';
  if (!mountPath.trim()) errors.mountPath = 'secret.validation.mountPathRequired';
  return errors;
}

export function validateKvPath(path: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!path.trim()) errors.path = 'secret.validation.pathRequired';
  return errors;
}

export function validateKvData(entries: Array<{ key: string; value: string }>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (entries.length === 0 || entries.every((e) => !e.key.trim())) {
    errors.data = 'secret.validation.dataRequired';
  }
  return errors;
}

// ============================================
// Typed-confirmation phrases
// ============================================

export function engineConfirmPhrase(engine: SecretEngine): string {
  return engine.mount_path;
}

export function certificateConfirmPhrase(cert: Certificate): string {
  return cert.serial_number;
}
